import Box from "@mui/material/Box";
import Container from "@mui/material/Container";

import { StyledAnchor } from "@/components/common/styles";
import { FRAME, MID, TINY, WIDE, marketingPalette } from "./tokens";
import DesktopNav from "./header/DesktopNav";
import { HeaderCountsProvider } from "./header/headerCounts";
import MobileNav from "./header/MobileNav";
import NavActions from "./header/NavActions";
import SalesInquiryDialog from "./SalesInquiryDialog";
import { HOME_HREF } from "./header/navigation";
import { BAR_ROW, BAR_Z_INDEX, GUTTER } from "./header/tokens";

/**
 * The published wordmark comes in two cuts, not one scaled: the wide bar uses a
 * 209×34 lockup and the collapsed bar a shorter 180×26 one. The wide one takes
 * no width at all, so it squashes between 992 and roughly 1150 where the menus
 * and buttons stop fitting, reproduced rather than fixed.
 */
const WORDMARK_IMG = {
  display: "inline-block",
  maxWidth: 1,
  verticalAlign: "middle",
};

const WORDMARK = {
  wide: {
    src: "/marketing/nav/webshare-wordmark.svg",
    link: { display: "block" },
    img: {},
  },
  compact: {
    src: "/marketing/nav/webshare-wordmark-compact.svg",
    link: { display: "block", mt: 3, [MID]: { pl: "10px" }, [TINY]: { pl: 0 } },
    img: { width: 180, [TINY]: { width: "140.8px" } },
  },
};

function Wordmark({ cut }: { cut: keyof typeof WORDMARK }) {
  const { src, link, img } = WORDMARK[cut];
  return (
    <StyledAnchor href={HOME_HREF} sx={link}>
      <Box
        component="img"
        src={src}
        alt="Webshare"
        sx={{ ...WORDMARK_IMG, ...img }}
      />
    </StyledAnchor>
  );
}

/**
 * The marketing site's header, reproduced from the published bar on
 * https://www.webshare.io/blog.
 *
 * It is fixed, as the published one is, and the pages underneath already carry
 * the top offset that clears it, the blog surface reproduces their spacing
 * exactly, so no spacer belongs here.
 *
 * `actions={false}` is the older bar one page still publishes
 * (`/enterprise-form`): the menus alone, with no Book a Call, Log In or Sign
 * Up in the bar or in the drawer.
 */
export default function MarketingHeader({
  actions = true,
}: {
  actions?: boolean;
}) {
  return (
    <Box
      component="header"
      sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: BAR_Z_INDEX }}
    >
      {/* The Locations menu's counts, written last into the bar they belong to. */}
      <HeaderCountsProvider>
        <Box
          sx={{
            position: "relative",
            backgroundColor: "background.default",
            borderBottom: `1px solid ${marketingPalette.hairline}`,
          }}
        >
          <Container
            disableGutters
            maxWidth={false}
            sx={{ maxWidth: `${FRAME.header}px`, px: `${GUTTER}px` }}
          >
            <Box
              component="nav"
              aria-label="Main"
              sx={{
                display: "none",
                [WIDE]: {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  height: `${BAR_ROW.desktop}px`,
                },
              }}
            >
              <Wordmark cut="wide" />
              <DesktopNav />
              {actions ? <NavActions variant="bar" /> : null}
            </Box>
            <Box
              sx={{
                // Flex, so the wordmark's own top margin cannot collapse out of
                // the row and push the whole bar down.
                display: "flex",
                alignItems: "flex-start",
                height: `${BAR_ROW.mobile}px`,
                [WIDE]: { display: "none" },
              }}
            >
              <Wordmark cut="compact" />
            </Box>
          </Container>
          <Box sx={{ [WIDE]: { display: "none" } }}>
            <MobileNav actions={actions} />
          </Box>
        </Box>
        {/* The page's one sales form, which every trigger on it opens. */}
        <SalesInquiryDialog />
      </HeaderCountsProvider>
    </Box>
  );
}
